import React, { useState, useEffect, useRef } from 'react';
import { 
  Calculator, 
  CheckCircle, 
  AlertCircle, 
  Loader, 
  Phone, 
  Mail, 
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Shield,
  Star,
  Check
} from 'lucide-react';

// ============================================================================
// PUBLIC QUOTE FORM - Customer-facing lead capture with instant estimates
// v2: Added friendly labels support for customer-friendly size selection
// ============================================================================

const PublicQuoteForm = ({ slug, onSuccess }) => {
  // -------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------
  
  // Business data (fetched from API)
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    serviceTypeId: '',
    serviceInterest: '',
    message: '',
    preferredSchedule: 'flexible',
    isDiscountEligible: false,
    // Honeypot
    website: '',
  });
  
  // Calculator state
  const [selectedService, setSelectedService] = useState(null);
  const [calculatorInputs, setCalculatorInputs] = useState({
    measurementValue: '',
    activeModifiers: {},
    selectedTier: '',
    selectedFriendlyLabel: '', // NEW: For friendly label selection
  });
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showExactInput, setShowExactInput] = useState(false); // Toggle for "I know exact measurement"
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  
  // Timing for spam prevention
  const formLoadedAt = useRef(new Date().toISOString());
  
  // -------------------------------------------------------------------------
  // API URL - Update this for your environment
  // -------------------------------------------------------------------------
  const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://suddyflow-api-cugygabvhfg6dvfj.eastus2-01.azurewebsites.net';

  // -------------------------------------------------------------------------
  // FETCH BUSINESS DATA
  // -------------------------------------------------------------------------
  
  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE}/api/public/business/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This business is not currently accepting online quote requests.');
          } else {
            setError('Unable to load. Please try again later.');
          }
          return;
        }
        
        const data = await response.json();
        setBusiness(data);
        setServices(data.services || []);
        
        // DEBUG: Log services to check modifier data
        console.log('Services loaded:', data.services);
        
      } catch (err) {
        console.error('Error fetching business:', err);
        setError('Unable to connect. Please check your internet connection.');
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      fetchBusiness();
    }
  }, [slug, API_BASE]);

  // -------------------------------------------------------------------------
  // SERVICE SELECTION HANDLER
  // -------------------------------------------------------------------------
  
  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === parseInt(serviceId));
    setSelectedService(service);
    setFormData(prev => ({ ...prev, serviceTypeId: serviceId }));
    
    // DEBUG: Log selected service to check modifiers
    console.log('Selected service:', service);
    console.log('Modifiers:', service?.modifiers);
    console.log('Friendly Labels:', service?.friendlyLabels);
    console.log('Use Friendly Labels:', service?.useFriendlyLabelsOnPublicForm);
    
    // Reset calculator
    setCalculatorInputs({
      measurementValue: '',
      activeModifiers: {},
      selectedTier: '',
      selectedFriendlyLabel: '',
    });
    setCalculatedQuote(null);
    setShowExactInput(false);
    
    // Show calculator if service has pricing template
    setShowCalculator(service?.hasCalculator || false);
  };

  // -------------------------------------------------------------------------
  // FRIENDLY LABEL SELECTION HANDLER
  // -------------------------------------------------------------------------
  
  const handleFriendlyLabelSelect = (label) => {
    const friendlyLabels = selectedService?.friendlyLabels || [];
    const selectedLabel = friendlyLabels.find(l => l.label === label);
    
    if (selectedLabel && selectedLabel.estimate) {
      setCalculatorInputs(prev => ({
        ...prev,
        selectedFriendlyLabel: label,
        measurementValue: selectedLabel.estimate.toString(),
      }));
    }
  };

  // -------------------------------------------------------------------------
  // CALCULATE ESTIMATED PRICE FOR A FRIENDLY LABEL
  // -------------------------------------------------------------------------
  
  const calculateLabelPrice = (labelOption) => {
    if (!selectedService || !labelOption?.estimate) return null;
    
    const { baseRate, minimumCharge } = selectedService;
    const estimate = parseFloat(labelOption.estimate) || 0;
    
    let price = estimate * (baseRate || 0);
    
    // Apply minimum charge if needed
    if (minimumCharge && price < minimumCharge) {
      price = minimumCharge;
    }
    
    return price;
  };

  // -------------------------------------------------------------------------
  // CALCULATOR LOGIC
  // -------------------------------------------------------------------------
  
  const calculateQuote = () => {
    if (!selectedService || !selectedService.hasCalculator) return null;
    
    const { pricingTemplate, baseRate, minimumCharge, tiers, modifiers } = selectedService;
    const { measurementValue, activeModifiers, selectedTier } = calculatorInputs;
    
    let baseAmount = 0;
    let total = 0;
    const breakdown = [];
    
    // Calculate based on pricing template
    switch (pricingTemplate) {
      case 'per-sqft':
      case 'per-linear-ft':
      case 'per-unit':
      case 'per-hour': {
        const value = parseFloat(measurementValue) || 0;
        if (value <= 0) return null;
        
        baseAmount = value * (baseRate || 0);
        breakdown.push({
          label: `${value.toLocaleString()} ${selectedService.measurementLabel || 'units'} × $${baseRate?.toFixed(2)}`,
          amount: baseAmount
        });
        break;
      }
      
      case 'tiered': {
        if (!selectedTier || !tiers) return null;
        const tier = tiers.find(t => t.name === selectedTier);
        if (!tier) return null;
        
        baseAmount = tier.price;
        breakdown.push({
          label: `${tier.name}${tier.description ? ` - ${tier.description}` : ''}`,
          amount: baseAmount
        });
        break;
      }
      
      case 'flat-rate': {
        baseAmount = baseRate || 0;
        breakdown.push({
          label: 'Flat Rate',
          amount: baseAmount
        });
        break;
      }
      
      default:
        return null;
    }
    
    total = baseAmount;
    
    // Apply minimum charge if needed (before modifiers)
    if (minimumCharge && total < minimumCharge) {
      total = minimumCharge;
      breakdown.push({
        label: 'Minimum charge applied',
        amount: minimumCharge - baseAmount
      });
    }
    
    // Apply modifiers
    const selectedModifierNames = Object.entries(activeModifiers)
      .filter(([_, isActive]) => isActive)
      .map(([name]) => name);
    
    // DEBUG: Log modifier calculation
    console.log('Active modifiers:', selectedModifierNames);
    console.log('Available modifiers:', modifiers);
    
    const appliedModifiers = [];
    
    if (modifiers && modifiers.length > 0) {
      selectedModifierNames.forEach(modName => {
        const modifier = modifiers.find(m => m.name === modName);
        console.log('Processing modifier:', modName, modifier);
        
        if (modifier) {
          let modAmount = 0;
          
          if (modifier.type === 'flat') {
            modAmount = parseFloat(modifier.amount) || 0;
          } else if (modifier.type === 'percent') {
            modAmount = total * ((parseFloat(modifier.amount) || 0) / 100);
          }
          
          console.log('Modifier amount:', modAmount);
          
          if (modAmount > 0) {
            total += modAmount;
            appliedModifiers.push({
              name: modifier.name,
              amount: modAmount,
              type: modifier.type
            });
          }
        }
      });
    }
    
    // Calculate range (±15%)
    const range = {
      low: Math.round(total * 0.85),
      high: Math.round(total * 1.15)
    };
    
    console.log('Final quote:', { total, range, appliedModifiers });
    
    return {
      serviceTypeId: selectedService.id,
      serviceTypeName: selectedService.name,
      pricingTemplate,
      measurementValue: parseFloat(measurementValue) || 0,
      measurementLabel: selectedService.measurementLabel,
      friendlyLabel: calculatorInputs.selectedFriendlyLabel || null, // Include selected friendly label
      baseRate,
      baseAmount,
      minimumCharge,
      activeModifiers: appliedModifiers,
      selectedTier,
      subtotal: baseAmount,
      total,
      range
    };
  };
  
  // Recalculate when inputs change
  useEffect(() => {
    if (selectedService?.hasCalculator) {
      const quote = calculateQuote();
      setCalculatedQuote(quote);
    }
  }, [calculatorInputs, selectedService]);

  // -------------------------------------------------------------------------
  // FORM SUBMISSION
  // -------------------------------------------------------------------------
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit if honeypot is filled
    if (formData.website) {
      // Silently "succeed" for bots
      setSubmitSuccess({
        message: "Thank you! We'll be in touch soon.",
      });
      return;
    }
    
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || null,
        address: formData.address.trim(),
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        zipCode: formData.zipCode.trim() || null,
        serviceTypeId: formData.serviceTypeId ? parseInt(formData.serviceTypeId) : null,
        serviceInterest: selectedService?.name || formData.serviceInterest || null,
        message: formData.message.trim() || null,
        preferredSchedule: formData.preferredSchedule,
        isDiscountEligible: formData.isDiscountEligible,
        quoteBreakdown: calculatedQuote,
        calculatorEstimate: calculatedQuote 
          ? `${calculatedQuote.range.low}-${calculatedQuote.range.high}`
          : null,
        website: formData.website, // Honeypot
        formLoadedAt: formLoadedAt.current,
      };
      
      const response = await fetch(`${API_BASE}/api/public/leads/${slug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }
      
      setSubmitSuccess(data);
      
      if (onSuccess) {
        onSuccess(data);
      }
      
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err.message || 'Unable to submit. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // INPUT HANDLERS
  // -------------------------------------------------------------------------
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleCalculatorChange = (field, value) => {
    setCalculatorInputs(prev => ({
      ...prev,
      [field]: value,
      // Clear friendly label if user types exact measurement
      ...(field === 'measurementValue' ? { selectedFriendlyLabel: '' } : {})
    }));
  };
  
  const toggleModifier = (modifierName) => {
    setCalculatorInputs(prev => ({
      ...prev,
      activeModifiers: {
        ...prev.activeModifiers,
        [modifierName]: !prev.activeModifiers[modifierName]
      }
    }));
  };

  // -------------------------------------------------------------------------
  // PHONE FORMATTING
  // -------------------------------------------------------------------------
  
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  // -------------------------------------------------------------------------
  // HELPER: Check if service should use friendly labels
  // -------------------------------------------------------------------------
  
  const shouldUseFriendlyLabels = () => {
    return selectedService?.useFriendlyLabelsOnPublicForm && 
           selectedService?.friendlyLabels && 
           selectedService.friendlyLabels.length > 0;
  };

  // -------------------------------------------------------------------------
  // HELPER: Format currency
  // -------------------------------------------------------------------------
  
  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // -------------------------------------------------------------------------
  // RENDER: LOADING STATE
  // -------------------------------------------------------------------------
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: ERROR STATE
  // -------------------------------------------------------------------------
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: SUCCESS STATE
  // -------------------------------------------------------------------------
  
  if (submitSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Request Received! 🎉
          </h2>
          
          <p className="text-gray-600 mb-6">
            {submitSuccess.message || business?.thankYouMessage || "Thank you! We'll be in touch soon."}
          </p>
          
          {/* Show quote range if available */}
          {submitSuccess.quoteRange && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-6 mb-6">
              <p className="text-green-100 text-sm mb-1">Your Estimated Quote</p>
              <p className="text-2xl sm:text-3xl font-bold">
                ${submitSuccess.quoteRange.low.toLocaleString()} - ${submitSuccess.quoteRange.high.toLocaleString()}
              </p>
              <p className="text-green-100 text-xs mt-2">
                Final price confirmed after on-site assessment
              </p>
            </div>
          )}
          
          {/* Response time */}
          <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
            <Clock className="w-5 h-5" />
            <span>We'll contact you {submitSuccess.responseTimeText || business?.responseTimeText || 'within 24 hours'}</span>
          </div>
          
          {/* Returning customer badge */}
          {submitSuccess.isReturningCustomer && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              <Star className="w-4 h-4" />
              Welcome back! We found your info on file.
            </div>
          )}
          
          {/* Contact info */}
          {business?.phone && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-gray-500 text-sm mb-2">Need immediate assistance?</p>
              <a 
                href={`tel:${business.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
              >
                <Phone className="w-5 h-5" />
                {business.phone}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: MAIN FORM
  // -------------------------------------------------------------------------
  
  const brandColor = business?.brandColor || '#3B82F6';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      
      {/* ========== ENHANCED PROFESSIONAL HEADER ========== */}
      <header className="relative overflow-hidden">
        {/* Background with gradient */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(180deg, ${brandColor} 0%, ${brandColor}ee 100%)`,
          }}
        />
        
        {/* Subtle decorative circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-20 -right-20 w-64 md:w-96 h-64 md:h-96 rounded-full opacity-10"
            style={{ backgroundColor: 'white' }}
          />
          <div 
            className="absolute -bottom-10 -left-10 w-48 md:w-72 h-48 md:h-72 rounded-full opacity-10"
            style={{ backgroundColor: 'white' }}
          />
        </div>
        
        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-4 py-6 sm:py-8 md:py-10 text-white">
          
          {/* Main Row: Logo+Info on left, Phone on right */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-6">
            
            {/* Left Group: Logo + Tagline/Badges */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5">
              
              {/* Logo */}
              {business?.logoUrl ? (
                <img 
                  src={business.logoUrl} 
                  alt={business.name}
                  className="h-36 sm:h-40 md:h-48 lg:h-52 w-auto object-contain drop-shadow-2xl"
                />
              ) : (
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold drop-shadow-md">
                  {business?.name}
                </h1>
              )}
              
              {/* Tagline & Trust Badges */}
              {(business?.tagline || business?.trustBadges) && (
                <div className="text-center sm:text-left">
                  {business?.tagline && (
                    <p className="text-white text-base sm:text-lg md:text-xl font-bold drop-shadow-lg">
                      {business.tagline}
                    </p>
                  )}
                  {business?.trustBadges && (
                    <p className="text-white/80 text-xs sm:text-sm md:text-base drop-shadow-lg font-medium">
                      {business.trustBadges}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Right: Phone Button */}
            {business?.showPhone !== false && business?.phone && (
              <div className="text-center sm:text-right">
                <a 
                  href={`tel:${business.phone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-2 sm:gap-3 px-5 sm:px-6 py-2.5 sm:py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full text-white font-bold text-lg sm:text-xl transition-all hover:scale-105 border border-white/30"
                >
                  <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span>{business.phone}</span>
                </a>
                <p className="text-white/70 text-sm mt-2 drop-shadow-lg font-medium">
                  Call for Your Free Estimate!
                </p>
              </div>
            )}
          </div>
          
          {/* Bottom Row: Headline & Service Area (centered) */}
          {(business?.headline || (business?.showServiceArea !== false && business?.city && business?.state)) && (
            <div className="text-center mt-6 sm:mt-8 pt-6 border-t border-white/20">
              {/* Headline - Only show if set */}
              {business?.headline && (
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
                  {business.headline}
                </h2>
              )}
              
              {/* Service Area */}
              {business?.showServiceArea !== false && business?.city && business?.state && (
                <p className="text-white/80 flex items-center justify-center gap-1.5 sm:gap-2 text-sm sm:text-base">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                  Serving {business.city}, {business.state} & surrounding areas
                </p>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Form */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Service Selection */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: brandColor }}
              >
                1
              </span>
              What service do you need?
            </h2>
            
            <select
              value={formData.serviceTypeId}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all text-lg"
              required
            >
              <option value="">Select a service...</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                  {service.hasCalculator && ' ✨'}
                </option>
              ))}
            </select>
            
            {selectedService?.description && (
              <p className="mt-3 text-gray-600 text-sm">{selectedService.description}</p>
            )}
          </div>

          {/* Quote Calculator (if service has pricing template) */}
          {selectedService?.hasCalculator && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-lg p-6 border border-amber-200">
              <button
                type="button"
                onClick={() => setShowCalculator(!showCalculator)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Calculator className="w-6 h-6 text-amber-600" />
                  <span>Get Instant Estimate</span>
                  <Sparkles className="w-5 h-5 text-amber-500" />
                </h2>
                {showCalculator ? (
                  <ChevronUp className="w-6 h-6 text-gray-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-400" />
                )}
              </button>
              
              {showCalculator && (
                <div className="mt-4 space-y-4">
                  
                  {/* ============================================ */}
                  {/* FRIENDLY LABELS SELECTION (NEW) */}
                  {/* ============================================ */}
                  {shouldUseFriendlyLabels() && !showExactInput && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedService.measurementLabel ? `Select ${selectedService.measurementLabel}` : 'Select Size'}
                      </label>
                      <div className="grid gap-2">
                        {selectedService.friendlyLabels.map(labelOption => {
  const estimatedPrice = calculateLabelPrice(labelOption);
  
  return (
    <button
      key={labelOption.label}
      type="button"
      onClick={() => handleFriendlyLabelSelect(labelOption.label)}
      className={`p-4 rounded-xl border-2 text-left transition-all ${
        calculatorInputs.selectedFriendlyLabel === labelOption.label
          ? 'border-amber-500 bg-amber-50'
          : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{labelOption.label}</p>
          {labelOption.description && (
            <p className="text-sm text-gray-500">{labelOption.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Show estimated price */}
          {estimatedPrice > 0 && (
            <span className="text-lg font-bold text-green-600">
              ~{formatCurrency(estimatedPrice)}
            </span>
          )}
          {calculatorInputs.selectedFriendlyLabel === labelOption.label && (
            <Check className="w-5 h-5 text-amber-600" />
          )}
        </div>
      </div>
    </button>
  );
})}
                      </div>
                      
                      {/* Toggle to exact input */}
                      <button
                        type="button"
                        onClick={() => setShowExactInput(true)}
                        className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
                      >
                        I know the exact {selectedService.measurementLabel?.toLowerCase() || 'measurement'}
                      </button>
                    </div>
                  )}
                  
                  {/* ============================================ */}
                  {/* EXACT MEASUREMENT INPUT */}
                  {/* ============================================ */}
                  {/* Show if: no friendly labels, OR user clicked "I know exact" */}
                  {(['per-sqft', 'per-linear-ft', 'per-unit', 'per-hour'].includes(selectedService.pricingTemplate)) && 
                   (!shouldUseFriendlyLabels() || showExactInput) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedService.measurementLabel || 'Measurement'}
                      </label>
                      <input
                        type="number"
                        value={calculatorInputs.measurementValue}
                        onChange={(e) => handleCalculatorChange('measurementValue', e.target.value)}
                        placeholder={`Enter ${selectedService.measurementLabel?.toLowerCase() || 'amount'}`}
                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-100 transition-all"
                        min="0"
                      />
                      {selectedService.measurementHelpText && (
                        <p className="mt-1 text-xs text-gray-500">{selectedService.measurementHelpText}</p>
                      )}
                      
                      {/* Toggle back to friendly labels */}
                      {shouldUseFriendlyLabels() && showExactInput && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowExactInput(false);
                            setCalculatorInputs(prev => ({
                              ...prev,
                              measurementValue: '',
                              selectedFriendlyLabel: '',
                            }));
                          }}
                          className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                          ← Back to size options
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Tier Selection (for tiered pricing) */}
                  {selectedService.pricingTemplate === 'tiered' && selectedService.tiers && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Size/Option
                      </label>
                      <div className="grid gap-2">
                        {selectedService.tiers.map(tier => (
                          <button
                            key={tier.name}
                            type="button"
                            onClick={() => handleCalculatorChange('selectedTier', tier.name)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              calculatorInputs.selectedTier === tier.name
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold text-gray-900">{tier.name}</p>
                                {tier.description && (
                                  <p className="text-sm text-gray-500">{tier.description}</p>
                                )}
                              </div>
                              <p className="text-lg font-bold text-amber-600">
                                ${tier.price?.toLocaleString()}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Modifiers (add-ons) */}
                  {selectedService.modifiers && selectedService.modifiers.filter(m => m.enabled !== false).length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Options
                      </label>
                      <div className="space-y-2">
                        {selectedService.modifiers.filter(m => m.enabled !== false).map(modifier => (
                          <label
                            key={modifier.name}
                            className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              calculatorInputs.activeModifiers[modifier.name]
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={calculatorInputs.activeModifiers[modifier.name] || false}
                              onChange={() => toggleModifier(modifier.name)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                              calculatorInputs.activeModifiers[modifier.name]
                                ? 'bg-amber-500 border-amber-500'
                                : 'border-gray-300'
                            }`}>
                              {calculatorInputs.activeModifiers[modifier.name] && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{modifier.name}</p>
                              {modifier.description && (
                                <p className="text-xs text-gray-500">{modifier.description}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Calculated Quote Display - MOBILE FRIENDLY */}
                  {calculatedQuote && calculatedQuote.total > 0 && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-4 sm:p-6 mt-4">
                      <p className="text-green-100 text-sm mb-1">Your Estimated Quote</p>
                      <p className="text-2xl sm:text-3xl font-bold">
                        ${calculatedQuote.range.low.toLocaleString()} - ${calculatedQuote.range.high.toLocaleString()}
                      </p>
                      {/* Show selected friendly label */}
                      {calculatedQuote.friendlyLabel && (
                        <p className="text-green-100 text-sm mt-1">
                          Based on: {calculatedQuote.friendlyLabel}
                        </p>
                      )}
                      {/* Show selected add-ons by name only */}
                      {calculatedQuote.activeModifiers && calculatedQuote.activeModifiers.length > 0 && (
                        <p className="text-green-100 text-sm mt-2">
                          Includes: {calculatedQuote.activeModifiers.map(m => m.name).join(', ')}
                        </p>
                      )}
                      <p className="text-green-100 text-xs mt-2">
                        * Estimate only. Final price confirmed after on-site assessment.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* No calculator message */}
          {selectedService && !selectedService.hasCalculator && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Custom Quote Required</p>
                <p className="text-sm text-blue-700">
                  We'll review your request and provide a personalized quote {business?.responseTimeText || 'within 24 hours'}.
                </p>
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: brandColor }}
              >
                2
              </span>
              Your Contact Information
            </h2>
            
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Smith"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                />
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  required
                  placeholder="(704) 555-1234"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                />
              </div>
            </div>
          </div>

          {/* Service Address */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: brandColor }}
              >
                3
              </span>
              Service Address
            </h2>
            
            <div className="space-y-4">
              {/* Street Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="123 Main Street"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                />
              </div>
              
              {/* City, State, Zip */}
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-6 sm:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Mooresville"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  />
                </div>
                <div className="col-span-3 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NC"
                    maxLength={2}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="28117"
                    maxLength={10}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
                style={{ backgroundColor: brandColor }}
              >
                4
              </span>
              Additional Details
            </h2>
            
            <div className="space-y-4">
              {/* Preferred Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  When do you need service?
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { value: 'asap', label: 'ASAP' },
                    { value: 'this_week', label: 'This Week' },
                    { value: 'next_week', label: 'Next Week' },
                    { value: 'flexible', label: 'Flexible' },
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, preferredSchedule: option.value }))}
                      className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        formData.preferredSchedule === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anything else we should know?
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Special instructions, access notes, specific areas to focus on..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all resize-none"
                />
              </div>
              
              {/* Discount Eligibility */}
              <label className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  name="isDiscountEligible"
                  checked={formData.isDiscountEligible}
                  onChange={handleInputChange}
                  className="mt-0.5 w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <p className="font-medium text-amber-900">
                    🎖️ Military, Veteran, First Responder, or Senior (65+)?
                  </p>
                  <p className="text-sm text-amber-700">
                    Check this box to receive your appreciation discount! (ID required at service)
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Honeypot - Hidden from humans */}
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
          />

          {/* Error Message */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Unable to Submit</p>
                <p className="text-sm text-red-700">{submitError}</p>
                {business?.phone && (
                  <p className="text-sm text-red-700 mt-1">
                    Please call us at <a href={`tel:${business.phone.replace(/\D/g, '')}`} className="font-semibold underline">{business.phone}</a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !formData.serviceTypeId}
            className="w-full py-4 px-6 rounded-xl font-bold text-lg text-white transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${brandColor}, ${brandColor}cc)`,
            }}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 animate-spin" />
                Submitting...
              </span>
            ) : calculatedQuote ? (
              <span className="flex items-center justify-center gap-2 flex-wrap">
                <span>Get My Quote</span>
                <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
                  ${calculatedQuote.range.low} - ${calculatedQuote.range.high}
                </span>
              </span>
            ) : (
              'Request My Free Quote'
            )}
          </button>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-gray-500 text-sm">
            <span className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Secure Form
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Response {business?.responseTimeText || 'within 24 hours'}
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              No Obligation
            </span>
          </div>
        </form>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-gray-500 text-sm">
        <p>Powered by <a href="https://suddyflow.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener">SuddyFlow</a></p>
      </footer>
    </div>
  );
};

export default PublicQuoteForm;